import { useCallback, useEffect, useRef, useState } from "react";
import { Play, WifiOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/i18n/useLanguage";
import { VideoSchema } from "@/components/site/VideoSchema";

interface Props {
  id: string;
  title: string;
  className?: string;
  ratio?: "16/9" | "9/16";
  /**
   * "high" → load thumbnail immediately (above-the-fold / featured).
   * "auto" → load when near viewport (default).
   */
  priority?: "high" | "auto";
}

type NetInfo = {
  saveData?: boolean;
  effectiveType?: "slow-2g" | "2g" | "3g" | "4g";
  addEventListener?: (t: string, cb: () => void) => void;
  removeEventListener?: (t: string, cb: () => void) => void;
};

function useSlowConnection() {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const nav = navigator as Navigator & { connection?: NetInfo };
    const c = nav.connection;
    if (!c) return;
    const update = () =>
      setSlow(Boolean(c.saveData) || c.effectiveType === "slow-2g" || c.effectiveType === "2g");
    update();
    c.addEventListener?.("change", update);
    return () => c.removeEventListener?.("change", update);
  }, []);
  return slow;
}

function useInView<T extends HTMLElement>(rootMargin = "600px", skip = false) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(skip);
  useEffect(() => {
    if (skip) {
      setInView(true);
      return;
    }
    const el = ref.current;
    if (!el || inView) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setInView(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView, rootMargin, skip]);
  return { ref, inView };
}

// De-dup hints across the page so we don't spam <head> with duplicate links.
const injectedHints = new Set<string>();
function injectHint(rel: "preconnect" | "prefetch", href: string, as?: string) {
  if (typeof document === "undefined") return;
  const key = `${rel}:${href}`;
  if (injectedHints.has(key)) return;
  injectedHints.add(key);
  const l = document.createElement("link");
  l.rel = rel;
  l.href = href;
  if (as) l.setAttribute("as", as);
  if (rel === "preconnect") l.crossOrigin = "";
  document.head.appendChild(l);
}

export function VideoEmbed({ id, title, className, ratio = "16/9", priority = "auto" }: Props) {
  const [active, setActive] = useState(false);
  const [thumbLoaded, setThumbLoaded] = useState(false);
  const [thumbSrc, setThumbSrc] = useState(`https://i.ytimg.com/vi/${id}/hqdefault.jpg`);
  const [thumbFailed, setThumbFailed] = useState(false);
  const slow = useSlowConnection();
  const { lang } = useLanguage();
  const en = lang === "en";
  const lightModeBadge = en ? "Light mode — tap to play" : "Lättviktsläge — tryck för att spela";
  const playLabel = (t: string) => en
    ? (slow ? `Play ${t} (light mode — may take longer)` : `Play ${t}`)
    : (slow ? `Spela ${t} (lättviktsläge — kan ta längre tid)` : `Spela ${t}`);
  const { ref, inView } = useInView<HTMLDivElement>("600px", priority === "high");
  const aspect = ratio === "9/16" ? "aspect-[9/16]" : "aspect-video";

  // Prefetch YouTube origins when about to enter viewport (only on decent connections)
  useEffect(() => {
    if (!inView || slow) return;
    injectHint("preconnect", "https://www.youtube-nocookie.com");
    injectHint("preconnect", "https://i.ytimg.com");
    injectHint("preconnect", "https://s.ytimg.com");
  }, [inView, slow]);

  // Warm the actual embed HTML on the first hover/focus so the click-to-play
  // feels instant. Uses prefetch (cache-only, no execution) — costs one tiny
  // HTML request per card, only after explicit user intent.
  const warmEmbed = useCallback(() => {
    if (slow || active) return;
    injectHint("prefetch", `https://www.youtube-nocookie.com/embed/${id}`, "document");
  }, [id, slow, active]);

  const handleThumbError = () => {
    if (!thumbFailed) {
      setThumbFailed(true);
      setThumbSrc(`https://i.ytimg.com/vi/${id}/hqdefault.jpg`);
    }
  };

  return (
    <div
      ref={ref}
      onPointerEnter={warmEmbed}
      onFocus={warmEmbed}
      className={cn(
        // Base look + lift/shadow only on devices that actually hover (skip touch)
        "group relative w-full overflow-hidden rounded-2xl bg-surface ring-1 ring-white/5 transition-[transform,box-shadow] duration-500 ease-out",
        "[@media(hover:hover)]:hover:-translate-y-1 [@media(hover:hover)]:hover:shadow-[0_30px_60px_-25px_rgba(0,0,0,0.7)] [@media(hover:hover)]:hover:ring-mint/40",
        "motion-reduce:transform-none motion-reduce:transition-none [@media(hover:hover)]:motion-reduce:hover:-translate-y-0 [@media(hover:hover)]:motion-reduce:hover:shadow-none",
        aspect,
        className,
      )}

    >
      {/* Video-SEO: varje inbäddad film får VideoObject-markup. */}
      <VideoSchema videos={[{ videoId: id, title }]} />
      {active ? (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&playsinline=1`}
          title={title}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 h-full w-full"
        />
      ) : (
        <button
          type="button"
          onClick={() => setActive(true)}
          className="absolute inset-0 h-full w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-mint/60"
          aria-label={playLabel(title)}
        >
          {/* Shimmer skeleton until the thumb actually paints */}
          <span
            aria-hidden
            className={cn(
              "absolute inset-0 transition-opacity duration-500",
              thumbLoaded ? "opacity-0" : "opacity-100",
            )}
          >
            <span className="absolute inset-0 bg-gradient-to-br from-white/[0.07] via-white/[0.03] to-white/[0.06]" />
            <span className="absolute inset-0 -translate-x-full bg-[linear-gradient(110deg,transparent_30%,rgba(255,255,255,0.08)_50%,transparent_70%)] motion-safe:animate-[shimmer_1.6s_ease-in-out_infinite]" />
          </span>
          {inView && (
            <img
              src={thumbSrc}
              alt={`Omslagsbild för filmen ${title} — producerad av Fenny`}
              aria-hidden
              loading={priority === "high" ? "eager" : "lazy"}
              decoding="async"
              fetchPriority={priority === "high" ? "high" : "low"}
              onLoad={(e) => {
                // YouTube returns a 120x90 grey placeholder for missing maxres — fall back
                const img = e.currentTarget;
                if (!thumbFailed && img.naturalWidth <= 120) {
                  handleThumbError();
                  return;
                }
                setThumbLoaded(true);
              }}
              onError={handleThumbError}
              className={cn(
                // Subtle zoom only on real hover devices; touch stays still
                "h-full w-full object-cover transition-[transform,opacity] duration-[1200ms] ease-out will-change-transform",
                "[@media(hover:hover)]:group-hover:scale-[1.06]",
                "motion-reduce:transition-none motion-reduce:group-hover:scale-100",
                thumbLoaded ? "opacity-100" : "opacity-0",
              )}
            />
          )}
          {/* Bottom scrim — slightly stronger by default so the title is readable on touch without hover */}
          <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-black/10 transition-opacity duration-500 [@media(hover:hover)]:group-hover:opacity-95 motion-reduce:transition-none" />
          {/* Mint radial glow — purely decorative, hover-only */}
          <span className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 [@media(hover:hover)]:group-hover:opacity-100 motion-reduce:transition-none [background:radial-gradient(60%_60%_at_50%_50%,rgba(66,255,201,0.18),transparent_70%)]" />
          {/* Title — fully visible at rest on touch; on hover devices it slides up subtly */}
          <span className="pointer-events-none absolute left-4 right-4 bottom-4 text-left text-sm font-medium text-white drop-shadow transition-transform duration-500 [@media(hover:hover)]:translate-y-1 [@media(hover:hover)]:group-hover:translate-y-0 motion-reduce:transition-none motion-reduce:translate-y-0">
            {title}
          </span>
          {/* Play CTA — no scale on touch or reduced motion */}
          <span className="pointer-events-none absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-mint text-mint-foreground shadow-[0_10px_30px_-8px_rgba(66,255,201,0.6)] transition-transform duration-500 ease-out [@media(hover:hover)]:group-hover:scale-110 motion-reduce:transition-none motion-reduce:group-hover:scale-100">
            <Play className="h-5 w-5 translate-x-[1px] fill-current" />
          </span>

          {slow && (
            <span className="pointer-events-none absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-black/65 px-2.5 py-1 text-[11px] font-medium text-white/90 ring-1 ring-white/15 backdrop-blur">
              <WifiOff className="h-3 w-3" />
              {lightModeBadge}
            </span>
          )}
        </button>
      )}
    </div>
  );
}
